import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, Instagram, ExternalLink } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

// ✅ STATIC GALLERY (NO BACKEND)
const STATIC_GALLERY = [
  {
    id: 1,
    url: "https://images.unsplash.com/photo-1560066984-138dadb4c035",
    media_type: "image",
    caption: "Hair Styling",
    tags: ["hair", "styling"],
  },
  {
    id: 2,
    url: "https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9",
    media_type: "image",
    caption: "Hair Color",
    tags: ["haircolor"],
  },
  {
    id: 3,
    url: "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2",
    media_type: "image",
    caption: "Bridal Look",
    tags: ["bridal"],
  },
];

const tags = ["all", "hair", "haircolor", "bridal", "styling"];

export default function Gallery() {
  const [gallery, setGallery] = useState([]);
  const [selectedTag, setSelectedTag] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Load once
  useEffect(() => {
    setGallery(STATIC_GALLERY);
    setLoading(false);
  }, []);

  // ✅ MEMOIZED gallery (fixes ESLint error)
  const safeGallery = useMemo(
    () => (Array.isArray(gallery) ? gallery : []),
    [gallery]
  );

  // ✅ MEMOIZED filtering (NO useEffect needed)
  const filteredGallery = useMemo(() => {
    if (selectedTag === "all") return safeGallery;
    return safeGallery.filter((item) =>
      Array.isArray(item.tags) && item.tags.includes(selectedTag)
    );
  }, [selectedTag, safeGallery]);

  return (
    <div className="pt-24">
      {/* Header */}
      <section className="section-padding py-16 bg-obsidian text-white text-center">
        <h1 className="font-heading text-5xl mb-4">Gallery</h1>
        <p className="text-white/70">Our latest transformations</p>
      </section>

      {/* Filters */}
      <section className="section-padding py-6 bg-white border-b sticky top-[72px] z-30">
        <div className="flex flex-wrap gap-2 justify-center">
          {tags.map((tag) => (
            <button
              key={tag}
              onClick={() => setSelectedTag(tag)}
              className={`px-4 py-2 rounded-full text-sm capitalize ${
                selectedTag === tag
                  ? "bg-obsidian text-white"
                  : "bg-neutral-100 text-neutral-600"
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="p-4">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="aspect-square" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {filteredGallery.map((item) => (
              <motion.div
                key={item.id}
                className="aspect-square overflow-hidden cursor-pointer"
                onClick={() => setLightbox(item)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <img
                  src={item.url}
                  alt={item.caption}
                  className="w-full h-full object-cover hover:scale-105 transition"
                />
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
            onClick={() => setLightbox(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <button className="absolute top-4 right-4 text-white">
              <X size={32} />
            </button>
            <img
              src={lightbox.url}
              alt={lightbox.caption}
              className="max-h-[80vh] max-w-[90vw]"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
